import { DashboardContent } from 'src/layouts/dashboard';
import { useTranslate } from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useRef, useState } from 'react';
import useWhitelistedUsers from './useWhitelistedUsers';
import { downloadWhitelistedUsersCSV } from 'src/pages/admin/services/whitelistService';

const UNLOCK_SOURCE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'code', label: 'Code' },
  { value: 'manual', label: 'Manual' },
  { value: 'volume', label: 'Volume' },
];

const View = () => {
  const translate = useTranslate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [unlockSource, setUnlockSource] = useState('');
  const [redemptionCodeInput, setRedemptionCodeInput] = useState('');
  const [redemptionCode, setRedemptionCode] = useState('');

  const { retrieve, records, total } = useWhitelistedUsers();

  useEffect(() => {
    retrieve(page + 1, rowsPerPage, search || undefined, unlockSource || undefined, redemptionCode || undefined);
  }, [page, rowsPerPage, search, unlockSource, redemptionCode, retrieve]);

  const handleSearch = () => {
    setSearch(searchInput);
    setRedemptionCode(redemptionCodeInput);
    setPage(0);
  };

  const handleSourceChange = (e: SelectChangeEvent<string>) => {
    setUnlockSource(e.target.value);
    setPage(0);
  };

  const handleExportCSV = async () => {
    try {
      const response = await downloadWhitelistedUsersCSV(
        search || undefined,
        unlockSource || undefined,
        redemptionCode || undefined
      );
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'whitelisted_users.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download CSV:', error);
    }
  };

  const formatDate = (value: string | number | null | undefined) => {
    if (!value) return '-';
    return new Date(typeof value === 'string' ? value : Number(value)).toLocaleString();
  };

  const getSourceLabel = (source: string) => {
    const map: Record<string, string> = {
      code: 'Code',
      manual: 'Manual',
      volume: 'Volume',
    };
    return map[source] ?? source ?? '-';
  };

  const getSourceColor = (source: string): 'primary' | 'secondary' | 'success' | 'default' => {
    const map: Record<string, 'primary' | 'secondary' | 'success'> = {
      code: 'primary',
      manual: 'secondary',
      volume: 'success',
    };
    return map[source] ?? 'default';
  };

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-users" action="read" fallback={<ErrorPage403 />}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="medium">
          {translate('whitelist.users.title')}
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder={translate('whitelist.users.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ flex: 1, minWidth: 220 }}
            />

            <TextField
              size="small"
              placeholder={translate('whitelist.users.redemptionCodePlaceholder')}
              value={redemptionCodeInput}
              onChange={(e) => setRedemptionCodeInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ flex: 1, minWidth: 220 }}
            />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{translate('whitelist.users.source')}</InputLabel>
              <Select
                value={unlockSource}
                label={translate('whitelist.users.source')}
                onChange={handleSourceChange}
              >
                {UNLOCK_SOURCE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
              {translate('whitelist.users.search')}
            </Button>

            <PermissionGuard resource="whitelist-users" action="export" fallback={null}>
              <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleExportCSV}>
                {translate('whitelist.exportCSV')}
              </Button>
            </PermissionGuard>
          </Box>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#fff',
                  '& th': {
                    backgroundColor: '#fff',
                    fontWeight: 600,
                  },
                }}
              >
                <TableCell>{translate('whitelist.users.address')}</TableCell>
                <TableCell>{translate('whitelist.users.email')}</TableCell>
                <TableCell>{translate('whitelist.users.whitelistSource')}</TableCell>
                <TableCell>{translate('whitelist.users.redeemCode')}</TableCell>
                <TableCell>{translate('whitelist.users.addedAt')}</TableCell>
                <TableCell align="center">{translate('whitelist.users.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {translate('whitelist.users.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {user.proxyWalletAddress || user.walletAddress || '-'}
                    </TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getSourceLabel(user.unlockSource)}
                        color={getSourceColor(user.unlockSource)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {user.redemptionCode || '-'}
                    </TableCell>
                    <TableCell>{formatDate(user.registeredAt)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.status || '-'}
                        color={user.status === 'ACTIVE' ? 'success' : 'default'}
                        size="small"
                        variant={user.status === 'ACTIVE' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </TableContainer>
      </Box>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
